/* eslint-disable react/react-in-jsx-scope */
import { FileInput, FileUploader } from "@/components/extension/file-upload";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import useImageCompressor from "@/hooks/useImageCompressor";
import usePostMultipart from "@/hooks/usePostMultipart";
import {
  Camera,
  FlipHorizontal,
  FlipVertical,
  Minus,
  MoveHorizontal,
  MoveVertical,
  Plus,
  RotateCcw,
  RotateCw,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

const PROFILE_PICTURE_SIZE = 500;
const PREVIEW_FRAME_WIDTH = 620;
const PREVIEW_FRAME_HEIGHT = 460;
const CROP_PREVIEW_SIZE = 320;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const getPreviewImageMetrics = (
  width,
  height,
  zoom = 1,
  rotation = 0,
  frameSize = { width: PREVIEW_FRAME_WIDTH, height: PREVIEW_FRAME_HEIGHT }
) => {
  if (!width || !height) {
    return {
      displayedWidth: 0,
      displayedHeight: 0,
      bounds: { x: 0, y: 0 },
    };
  }

  const frameWidth = frameSize?.width || PREVIEW_FRAME_WIDTH;
  const frameHeight = frameSize?.height || PREVIEW_FRAME_HEIGHT;
  const imageRatio = width / height;
  const frameRatio = frameWidth / frameHeight;
  const displayedWidth =
    imageRatio > frameRatio ? frameWidth : frameHeight * imageRatio;
  const displayedHeight =
    imageRatio > frameRatio ? frameWidth / imageRatio : frameHeight;
  const radians = (rotation * Math.PI) / 180;
  const rotatedWidth =
    displayedWidth * Math.abs(Math.cos(radians)) +
    displayedHeight * Math.abs(Math.sin(radians));
  const rotatedHeight =
    displayedWidth * Math.abs(Math.sin(radians)) +
    displayedHeight * Math.abs(Math.cos(radians));

  return {
    displayedWidth,
    displayedHeight,
    bounds: {
      x: Math.max(0, (rotatedWidth * zoom - CROP_PREVIEW_SIZE) / 2),
      y: Math.max(0, (rotatedHeight * zoom - CROP_PREVIEW_SIZE) / 2),
    },
  };
};

const getCroppedProfilePicture = async (
  file,
  previewUrl,
  zoom,
  cropPosition,
  rotation,
  mirror,
  frameSize
) => {
  const image = await loadImage(previewUrl);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    return null;
  }

  canvas.width = PROFILE_PICTURE_SIZE;
  canvas.height = PROFILE_PICTURE_SIZE;
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, PROFILE_PICTURE_SIZE, PROFILE_PICTURE_SIZE);

  const metrics = getPreviewImageMetrics(
    image.naturalWidth,
    image.naturalHeight,
    zoom,
    rotation,
    frameSize
  );
  const outputScale = PROFILE_PICTURE_SIZE / CROP_PREVIEW_SIZE;
  const previewTranslateX = cropPosition.x * metrics.bounds.x;
  const previewTranslateY = cropPosition.y * metrics.bounds.y;

  context.translate(PROFILE_PICTURE_SIZE / 2, PROFILE_PICTURE_SIZE / 2);
  context.translate(-previewTranslateX * outputScale, -previewTranslateY * outputScale);
  context.scale(outputScale * zoom, outputScale * zoom);
  context.rotate((rotation * Math.PI) / 180);
  context.scale(mirror.x ? -1 : 1, mirror.y ? -1 : 1);
  context.drawImage(
    image,
    -metrics.displayedWidth / 2,
    -metrics.displayedHeight / 2,
    metrics.displayedWidth,
    metrics.displayedHeight
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null);
          return;
        }

        resolve(
          new File([blob], file.name, {
            type: blob.type || file.type || "image/jpeg",
          })
        );
      },
      "image/jpeg",
      0.9
    );
  });
};

const ManagePictureProfil = ({
  setIdMedia,
  loadingEditProfil,
  photoUrl,
  setPhotoUrl,
}) => {
  const { handleCompressImage } = useImageCompressor();
  const { postMultipart } = usePostMultipart();
  const [uploadLogoLoading, setUploadLogoLoading] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [fileToCrop, setFileToCrop] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [cropZoom, setCropZoom] = useState(1);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [cropRotation, setCropRotation] = useState(0);
  const [cropMirror, setCropMirror] = useState({ x: false, y: false });
  const [cropImageSize, setCropImageSize] = useState({ width: 0, height: 0 });
  const [cropPreviewFrameSize, setCropPreviewFrameSize] = useState({
    width: PREVIEW_FRAME_WIDTH,
    height: PREVIEW_FRAME_HEIGHT,
  });
  const cropFrameRef = useRef(null);
  const dragStartRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    const frame = cropFrameRef.current;
    if (!frame || !cropDialogOpen) {
      return;
    }

    const syncFrameSize = () => {
      const rect = frame.getBoundingClientRect();
      setCropPreviewFrameSize({
        width: rect.width || PREVIEW_FRAME_WIDTH,
        height: rect.height || PREVIEW_FRAME_HEIGHT,
      });
    };

    syncFrameSize();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(syncFrameSize);
    observer.observe(frame);

    return () => observer.disconnect();
  }, [cropDialogOpen]);

  const resetCrop = () => {
    setCropDialogOpen(false);
    setFileToCrop(null);
    setPreviewUrl("");
    setCropZoom(1);
    setCropPosition({ x: 0, y: 0 });
    setCropRotation(0);
    setCropMirror({ x: false, y: false });
    setCropImageSize({ width: 0, height: 0 });
    dragStartRef.current = null;
  };

  const getPreviewImageBounds = (zoom = cropZoom, rotation = cropRotation) => {
    return getPreviewImageMetrics(
      cropImageSize.width,
      cropImageSize.height,
      zoom,
      rotation,
      cropPreviewFrameSize
    ).bounds;
  };

  const getBoundedCropPosition = (
    position,
    zoom = cropZoom,
    rotation = cropRotation
  ) => {
    const bounds = getPreviewImageBounds(zoom, rotation);

    return {
      x: bounds.x > 0 ? clamp(position.x, -1, 1) : 0,
      y: bounds.y > 0 ? clamp(position.y, -1, 1) : 0,
    };
  };

  const handlePictureChange = async (value) => {
    if (value?.[0]) {
      const objectUrl = URL.createObjectURL(value[0]);

      setFileToCrop(value[0]);
      setPreviewUrl(objectUrl);
      setCropZoom(1);
      setCropPosition({ x: 0, y: 0 });
      setCropRotation(0);
      setCropMirror({ x: false, y: false });
      setCropImageSize({ width: 0, height: 0 });
      setCropDialogOpen(true);
      loadImage(objectUrl)
        .then((image) => {
          setCropImageSize({
            width: image.naturalWidth,
            height: image.naturalHeight,
          });
        })
        .catch(() => {
          setCropImageSize({ width: 0, height: 0 });
        });
    }
  };

  const handleCropPointerDown = (event) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      position: cropPosition,
    };
  };

  const handleCropPointerMove = (event) => {
    if (!dragStartRef.current) {
      return;
    }

    const bounds = getPreviewImageBounds();
    const nextX =
      bounds.x > 0
        ? dragStartRef.current.position.x -
          (event.clientX - dragStartRef.current.x) / bounds.x
        : 0;
    const nextY =
      bounds.y > 0
        ? dragStartRef.current.position.y -
          (event.clientY - dragStartRef.current.y) / bounds.y
        : 0;

    setCropPosition(getBoundedCropPosition({ x: nextX, y: nextY }));
  };

  const handleCropPointerUp = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragStartRef.current = null;
  };

  const uploadProfilePicture = async () => {
    if (!fileToCrop || !previewUrl) {
      return;
    }

    setUploadLogoLoading(true);
    try {
      const data = new FormData();
      const croppedFile = await getCroppedProfilePicture(
        fileToCrop,
        previewUrl,
        cropZoom,
        cropPosition,
        cropRotation,
        cropMirror,
        cropPreviewFrameSize
      );
      if (!croppedFile) {
        toast({
          title: "Impossible de recadrer l'image",
          duration: 1000,
        });
        return;
      }
      const originalFile = croppedFile;

      data.append("type", 3);

      // Compresser l'image avant l'upload
      const compressedBlob = await handleCompressImage(originalFile, 500, 300, 0.9);
      if (compressedBlob) {
        data.append("files[]", compressedBlob, originalFile.name);
      } else {
        data.append("files[]", originalFile);
      }

      const newMedias = await postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data,
      });
      if (!Array.isArray(newMedias) && newMedias?.success === false) {
        toast({
          title: newMedias.message || "Erreur lors de l'envoi de l'image",
          duration: 1000,
        });
        setUploadLogoLoading(false);
        return;
      }

      const uploadedMedia = Array.isArray(newMedias)
        ? newMedias[0]
        : Array.isArray(newMedias?.data)
          ? newMedias.data[0]
          : newMedias?.data;

      if (!uploadedMedia?.uri || !uploadedMedia?.id) {
        toast({
          title: "Réponse invalide lors de l'envoi de l'image",
          duration: 1000,
        });
        return;
      }

      setPhotoUrl(uploadedMedia.uri);
      setIdMedia(uploadedMedia.id);
      resetCrop();
    } catch (error) {
      console.error("Erreur pendant le traitement de la photo de profil :", error);
      toast({
        title: "Erreur pendant le traitement de l'image",
        duration: 1000,
      });
    } finally {
      setUploadLogoLoading(false);
    }
  };

  const imageSrc =
    photoUrl?.startsWith("blob:") || photoUrl?.startsWith("http")
      ? photoUrl
      : photoUrl
        ? import.meta.env.VITE_MEDIA_BASE + photoUrl
        : "";
  const cropBounds = getPreviewImageBounds();
  const previewTranslateX = cropPosition.x * cropBounds.x;
  const previewTranslateY = cropPosition.y * cropBounds.y;

  if (loadingEditProfil) return <div>Chargement...</div>;
  return (
    <>
      <div className="h-full flex items-center justify-center relative overflow-hidden rounded-full">
        {imageSrc && (
          <img
            src={imageSrc}
            alt="photo de profil"
            className="absolute top-0 left-0 size-full rounded-full object-cover"
          />
        )}
        <FileUploader
          value={[]}
          onValueChange={handlePictureChange}
          dropzoneOptions={{
            multiple: false,
            accept: {
              "image/*": [".jpeg", ".jpg", ".png", ".webp"],
            },
          }}
          className={`${
            photoUrl
              ? "absolute top-0 left-0 z-50 bg-background opacity-50 w-1/2 rounded-full"
              : "relative size-full"
          }`}
        >
          <FileInput className="size-full rounded-full border border-dashed border-background/50 relative flex justify-center items-center">
            {uploadLogoLoading ? (
              <div className="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="animate-spin"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              </div>
            ) : (
              <div className="bg-none size-8 p-0 rounded-full flex justify-center items-center">
                <Camera size={18} />
              </div>
            )}
          </FileInput>
        </FileUploader>
      </div>

      <Dialog open={cropDialogOpen} onOpenChange={(open) => !open && resetCrop()}>
        <DialogContent
          className="z-[100001] flex max-h-[calc(100vh-2rem)] min-w-0 max-w-[980px] flex-col overflow-hidden rounded-2xl border-0 p-0 shadow-2xl"
          overlayClassName="z-[100000]"
        >
          <DialogHeader className="shrink-0 border-b px-6 py-5">
            <DialogTitle className="text-xl">Modifier la photo</DialogTitle>
          </DialogHeader>

          <div className="grid min-h-0 flex-1 overflow-y-auto gap-0 md:grid-cols-[minmax(0,1fr)_300px]">
            <div className="flex min-h-[420px] items-center justify-center bg-[radial-gradient(circle_at_center,#ffffff_0,#f4f7f2_45%,#e8eee5_100%)] px-8 py-10 md:min-h-[520px]">
              <div
                ref={cropFrameRef}
                className="relative flex h-[460px] w-full max-w-[620px] touch-none items-center justify-center overflow-hidden rounded-2xl border border-[#9cab94]/25 bg-[linear-gradient(45deg,rgba(29,82,20,0.06)_25%,transparent_25%,transparent_75%,rgba(29,82,20,0.06)_75%),linear-gradient(45deg,rgba(29,82,20,0.06)_25%,transparent_25%,transparent_75%,rgba(29,82,20,0.06)_75%)] bg-[length:22px_22px] bg-[position:0_0,11px_11px] shadow-sm cursor-grab active:cursor-grabbing"
                onPointerDown={handleCropPointerDown}
                onPointerMove={handleCropPointerMove}
                onPointerUp={handleCropPointerUp}
                onPointerCancel={handleCropPointerUp}
              >
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt="photo a recadrer"
                    className="max-h-full max-w-full select-none object-contain"
                    draggable={false}
                    style={{
                      transform: `translate(${
                        -previewTranslateX
                      }px, ${
                        -previewTranslateY
                      }px) scale(${cropZoom}) rotate(${cropRotation}deg) scaleX(${
                        cropMirror.x ? -1 : 1
                      }) scaleY(${cropMirror.y ? -1 : 1})`,
                    }}
                  />
                )}
                <div className="pointer-events-none absolute left-1/2 top-1/2 size-80 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-white shadow-[0_0_0_9999px_rgba(21,32,18,0.38),0_18px_60px_rgba(35,49,31,0.25)]" />
                <div className="pointer-events-none absolute left-1/2 top-1/2 size-80 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#6d7f66]/45" />
              </div>
            </div>

            <div className="space-y-5 border-l bg-background px-6 py-5">
              <div className="space-y-2">
                <p className="text-sm font-medium">Ajustements</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const nextRotation = clamp(cropRotation - 90, -180, 180);
                      setCropRotation(nextRotation);
                      setCropPosition((current) =>
                        getBoundedCropPosition(current, cropZoom, nextRotation)
                      );
                    }}
                  >
                    <RotateCcw className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const nextRotation = clamp(cropRotation + 90, -180, 180);
                      setCropRotation(nextRotation);
                      setCropPosition((current) =>
                        getBoundedCropPosition(current, cropZoom, nextRotation)
                      );
                    }}
                  >
                    <RotateCw className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      setCropMirror((current) => ({
                        ...current,
                        x: !current.x,
                      }))
                    }
                  >
                    <FlipHorizontal className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      setCropMirror((current) => ({
                        ...current,
                        y: !current.y,
                      }))
                    }
                  >
                    <FlipVertical className="size-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <Minus className="size-4 shrink-0 text-muted-foreground" />
                  <input
                    id="profile-picture-zoom"
                    type="range"
                    min="1"
                    max="2.5"
                    step="0.05"
                    value={cropZoom}
                    onChange={(event) => {
                      const nextZoom = Number(event.target.value);
                      setCropZoom(nextZoom);
                      setCropPosition((current) =>
                        getBoundedCropPosition(current, nextZoom)
                      );
                    }}
                    className="h-1.5 w-full cursor-pointer rounded-full bg-muted accent-primary"
                    aria-label="Zoom"
                  />
                  <Plus className="size-4 shrink-0 text-muted-foreground" />
                </div>

                <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2">
                  <RotateCw className="size-4 shrink-0 text-muted-foreground" />
                  <input
                    id="profile-picture-rotation"
                    type="range"
                    min="-180"
                    max="180"
                    step="1"
                    value={cropRotation}
                    onChange={(event) => {
                      const nextRotation = Number(event.target.value);
                      setCropRotation(nextRotation);
                      setCropPosition((current) =>
                        getBoundedCropPosition(current, cropZoom, nextRotation)
                      );
                    }}
                    className="h-1.5 w-full cursor-pointer rounded-full bg-background accent-primary"
                    aria-label="Rotation"
                  />
                </div>

                <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2">
                  <MoveHorizontal className="size-4 shrink-0 text-muted-foreground" />
                  <input
                    id="profile-picture-x"
                    type="range"
                    min="-1"
                    max="1"
                    step="0.02"
                    value={cropPosition.x}
                    onChange={(event) =>
                      setCropPosition((current) =>
                        getBoundedCropPosition({
                          ...current,
                          x: Number(event.target.value),
                        })
                      )
                    }
                    className="h-1.5 w-full cursor-pointer rounded-full bg-background accent-primary"
                    aria-label="Position horizontale"
                  />
                </div>
                <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2">
                  <MoveVertical className="size-4 shrink-0 text-muted-foreground" />
                  <input
                    id="profile-picture-y"
                    type="range"
                    min="-1"
                    max="1"
                    step="0.02"
                    value={cropPosition.y}
                    onChange={(event) =>
                      setCropPosition((current) =>
                        getBoundedCropPosition({
                          ...current,
                          y: Number(event.target.value),
                        })
                      )
                    }
                    className="h-1.5 w-full cursor-pointer rounded-full bg-background accent-primary"
                    aria-label="Position verticale"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t bg-muted/20 px-6 py-4">
            <Button type="button" variant="outline" onClick={resetCrop}>
              Annuler
            </Button>
            <Button
              type="button"
              onClick={uploadProfilePicture}
              disabled={uploadLogoLoading}
            >
              {uploadLogoLoading ? "Chargement..." : "Valider"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManagePictureProfil;
